import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

import {
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  FALLBACK_LNG,
  LOCALE_ALIASES,
  isSupportedLocale,
} from './locales'

import plCommon from './locales/pl/common.json'
import plAuth from './locales/pl/auth.json'
import plErrors from './locales/pl/errors.json'
import plSubscriptions from './locales/pl/subscriptions.json'
import plAllegro from './locales/pl/allegro.json'
import plEmag from './locales/pl/emag.json'
import plProfile from './locales/pl/profile.json'
import plDashboard from './locales/pl/dashboard.json'
import plTours from './locales/pl/tours.json'
import plAccounts from './locales/pl/accounts.json'
import plCredits from './locales/pl/credits.json'
import plReferrals from './locales/pl/referrals.json'
import plPartners from './locales/pl/partners.json'
import plAdminSiteNotice from './locales/pl/adminSiteNotice.json'
import plAdminAdsAgency from './locales/pl/adminAdsAgency.json'
import plFeatureSurvey from './locales/pl/featureSurvey.json'
import plOnboardingAdmin from './locales/pl/onboardingAdmin.json'
import plTasks from './locales/pl/tasks.json'
import enCommon from './locales/en/common.json'
import enAuth from './locales/en/auth.json'
import enErrors from './locales/en/errors.json'
import enSubscriptions from './locales/en/subscriptions.json'
import enAllegro from './locales/en/allegro.json'
import enEmag from './locales/en/emag.json'
import enProfile from './locales/en/profile.json'
import enDashboard from './locales/en/dashboard.json'
import enTours from './locales/en/tours.json'
import enAccounts from './locales/en/accounts.json'
import enCredits from './locales/en/credits.json'
import enReferrals from './locales/en/referrals.json'
import enPartners from './locales/en/partners.json'
import enAdminSiteNotice from './locales/en/adminSiteNotice.json'
import enAdminAdsAgency from './locales/en/adminAdsAgency.json'
import enFeatureSurvey from './locales/en/featureSurvey.json'
import enOnboardingAdmin from './locales/en/onboardingAdmin.json'
import enTasks from './locales/en/tasks.json'
import csCommon from './locales/cs/common.json'
import csAuth from './locales/cs/auth.json'
import csErrors from './locales/cs/errors.json'
import csSubscriptions from './locales/cs/subscriptions.json'
import csAllegro from './locales/cs/allegro.json'
import csEmag from './locales/cs/emag.json'
import csProfile from './locales/cs/profile.json'
import csDashboard from './locales/cs/dashboard.json'
import csTours from './locales/cs/tours.json'
import csAccounts from './locales/cs/accounts.json'
import csCredits from './locales/cs/credits.json'
import csReferrals from './locales/cs/referrals.json'
import csPartners from './locales/cs/partners.json'
import csAdminSiteNotice from './locales/cs/adminSiteNotice.json'
import csAdminAdsAgency from './locales/cs/adminAdsAgency.json'
import csFeatureSurvey from './locales/cs/featureSurvey.json'
import csOnboardingAdmin from './locales/cs/onboardingAdmin.json'
import csTasks from './locales/cs/tasks.json'

const NAMESPACES = [
  'common',
  'auth',
  'errors',
  'subscriptions',
  'allegro',
  'emag',
  'profile',
  'dashboard',
  'tours',
  'accounts',
  'credits',
  'referrals',
  'partners',
  'adminSiteNotice',
  'adminAdsAgency',
  'featureSurvey',
  'onboardingAdmin',
  'tasks',
] as const

// Detection order for anonymous / pre-auth visits:
//   1. localStorage 'i18nextLng' (set by LanguageSelector or previous session)
//   2. navigator.language (browser preference, with sk → cs alias)
//   3. fallback → DEFAULT_LOCALE ('pl')
//
// For authenticated users, LanguageProvider reads authStore.user.language and
// calls i18n.changeLanguage() immediately on mount — that takes priority over
// whatever the detector resolved during init, matching the resolution order:
//   user.language → localStorage → navigator → pl
i18next
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      pl: {
        common: plCommon,
        auth: plAuth,
        errors: plErrors,
        subscriptions: plSubscriptions,
        allegro: plAllegro,
        emag: plEmag,
        profile: plProfile,
        dashboard: plDashboard,
        tours: plTours,
        accounts: plAccounts,
        credits: plCredits,
        referrals: plReferrals,
        partners: plPartners,
        adminSiteNotice: plAdminSiteNotice,
        adminAdsAgency: plAdminAdsAgency,
        featureSurvey: plFeatureSurvey,
        onboardingAdmin: plOnboardingAdmin,
        tasks: plTasks,
      },
      en: {
        common: enCommon,
        auth: enAuth,
        errors: enErrors,
        subscriptions: enSubscriptions,
        allegro: enAllegro,
        emag: enEmag,
        profile: enProfile,
        dashboard: enDashboard,
        tours: enTours,
        accounts: enAccounts,
        credits: enCredits,
        referrals: enReferrals,
        partners: enPartners,
        adminSiteNotice: enAdminSiteNotice,
        adminAdsAgency: enAdminAdsAgency,
        featureSurvey: enFeatureSurvey,
        onboardingAdmin: enOnboardingAdmin,
        tasks: enTasks,
      },
      cs: {
        common: csCommon,
        auth: csAuth,
        errors: csErrors,
        subscriptions: csSubscriptions,
        allegro: csAllegro,
        emag: csEmag,
        profile: csProfile,
        dashboard: csDashboard,
        tours: csTours,
        accounts: csAccounts,
        credits: csCredits,
        referrals: csReferrals,
        partners: csPartners,
        adminSiteNotice: csAdminSiteNotice,
        adminAdsAgency: csAdminAdsAgency,
        featureSurvey: csFeatureSurvey,
        onboardingAdmin: csOnboardingAdmin,
        tasks: csTasks,
      },
    },
    ns: [...NAMESPACES],
    defaultNS: 'common',
    fallbackLng: FALLBACK_LNG,
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    nonExplicitSupportedLngs: true,
    returnEmptyString: false,
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage'],
      convertDetectedLanguage: (lng: string) => {
        const primary = lng.split(/[-_]/)[0]?.toLowerCase() ?? ''
        if (LOCALE_ALIASES[primary]) return LOCALE_ALIASES[primary]
        if (isSupportedLocale(primary)) return primary
        return lng
      },
    },
  })

export default i18next
